// Adaptive gamma compression for long multiplicative penalty chains.
//
// The pathology this fixes: five independent factors at ~0.9 multiply to
// 0.59. A day that is "all green but nothing perfect" gets demoted a full
// band, and the more factors a sport scores, the harsher the demotion,
// purely as an artefact of chain length. Diving shipped the fix first
// (adaptive gamma); this module generalises it so every long chain shares
// one mechanism with chain-length-appropriate parameters.
//
//   compressed = product^(base + scale * product)
//
// The exponent grows with the product, so strong days are barely touched
// (0.95 stays ~0.95) while honest-but-imperfect products get a meaningful
// lift (0.5 lifts to ~0.6 on the short-chain parameters). Bad days stay
// bad: a 0.01 product still reads ~0.16, inside the bottom band.
//
// Parameters scale with chain length because the log-penalty of
// "everything at 0.9" grows linearly with the number of factors. A longer
// chain needs a smaller exponent to land the same honest day in the same
// band:
//
//   preset        base  scale  declared factor count
//   SHORT_CHAIN   0.55  0.35   length <= 6   (diving 5, wind 6, wing 6, swim 5, off-piste 5)
//   MEDIUM_CHAIN  0.45  0.40   7 <= length <= 10 (surf 8, bodyboard 10, SUP 7, piste 8, touring 9, MTB 8/9, downwind 8)
//   LONG_CHAIN    0.40  0.40   length >= 11  (prone foil 11)
//
// Calibration anchors (product -> compressed):
//   short:  all-0.9 5-factor day  0.59 -> 0.67   "all green" stays near Great
//   medium: all-0.9 9-factor day  0.39 -> 0.56   bit-of-everything reads ~6
//   long:   the audit's 11-factor "bit of everything" 0.48 -> 0.65
//   any:    perfect day 1.0 -> 1.0 (untouched)
//   any:    shocker 0.01 -> ~0.16 (still the bottom band)
//
// Active scorers pin their calibrated preset explicitly. Factor-count lookup
// remains available for compatibility and experiments, but adding a neutral
// explanation factor must never change a production sport's calibration.

export type GammaParams = { base: number; scale: number };

export const GAMMA_SHORT_CHAIN: GammaParams = { base: 0.55, scale: 0.35 };
export const GAMMA_MEDIUM_CHAIN: GammaParams = { base: 0.45, scale: 0.4 };
export const GAMMA_LONG_CHAIN: GammaParams = { base: 0.4, scale: 0.4 };

// Length->preset thresholds (tracker P6-1). Inclusive upper bounds. Chosen so
// the original six gamma chains keep their preset: diving 5 / wind 6 / wing 6
// stay SHORT, downwind 8 / MTB 9 stay MEDIUM, prone 11 stays LONG.
export const GAMMA_SHORT_MAX_FACTORS = 6;
export const GAMMA_MEDIUM_MAX_FACTORS = 10;

/**
 * Derive a chain's gamma preset from its declared condition-factor count
 * (drivers + modifiers; structural scale/bonus terms do not count). This is the
 * Phase 6 all-or-none policy: every multiplicative chain is compressed by the
 * same mechanism, parameterised only by how many factors it stacks.
 */
export function presetForChainLength(length: number): GammaParams {
  if (length <= GAMMA_SHORT_MAX_FACTORS) return GAMMA_SHORT_CHAIN;
  if (length <= GAMMA_MEDIUM_MAX_FACTORS) return GAMMA_MEDIUM_CHAIN;
  return GAMMA_LONG_CHAIN;
}

/**
 * Compress a multiplicative chain product (0..~1.05) with an adaptive
 * exponent. Products above 1 (small multiplicative bonuses) pass through
 * unchanged; callers clamp to their scale afterwards.
 */
export function adaptiveGamma(product: number, params: GammaParams): number {
  if (product <= 0) return 0;
  if (product >= 1) return product;
  const gamma = params.base + params.scale * product;
  return Math.pow(product, gamma);
}

// Top-band reservation for daily sport scores (tracker P11-5 / finding R1;
// Josh's 2026-06-24 "reserve the top for exceptional" call).
//
// After the Phase 6 gamma + the Phase 7 grey-day de-dup, a merely-very-good day
// (clear, cold, deep base, ordinary grooming - no fresh snow / standout package)
// reached the 9-10 band: the campaign-2 distribution sweep found piste 9+ at
// 11.5% and MTB at 16.7% of in-season days, far too common for "the top score".
// The 9+ days are genuine good conditions, not junk, but the philosophy reserves
// the TOP band for the genuinely exceptional ("people would agree it is a 9").
//
// This is a top-only reservation applied to daily-style scores, not per hour:
//   - at or below the threshold (display 8.0), the score is UNTOUCHED, so the
//     generous mid is preserved exactly (a "great" 7-8 day stays a 7-8 day);
//   - above it, the [T, 1] range is compressed by an exponent k, so reaching 9+
//     requires the full factor package near-perfect, while a genuinely
//     exceptional day (product near 1.0) still reaches 9.5-10.
// k is per-sport because each sport's top band has a different shape. Foils and
// the other coastal and mountain sports do NOT use this. MTB and Diving apply
// the transform to the final daily composite. Piste applies it inside each
// representative band before the 0.65/0.35 blend, which is the blessed v9
// distribution; do not move that ordering without a versioned recalibration.
export const TOP_RESERVE_THRESHOLD = 0.8;

// Per-sport reservation exponents calibrated on stored daily distributions.
// Piste 1.8 moved 9+ from ~11.5% to ~2.3% in the campaign-3 calibration. MTB's
// original k=3 target did not hold as coverage matured: current-version
// 7,350 completed days measured 16.245% at 9+, while k=6 measures 5.116%.
// Diving's 5,699 completed days measured 11.879% at 9+; k=2 measures 2.930%
// while its measured crystal-water golden still rounds to 9.0. These transforms
// affect only daily scores above 8.0. MTB's constant is also used by its
// per-trail planner mirror so headline and per-trail scores stay in parity.
export const PISTE_TOP_RESERVE_K = 1.8;
export const MTB_TOP_RESERVE_K = 6.0;
export const DIVING_TOP_RESERVE_K = 2.0;

export function reserveTopBand(
  product: number,
  k: number,
  threshold = TOP_RESERVE_THRESHOLD,
): number {
  if (k <= 1 || product <= threshold || product >= 1) return product;
  const span = 1 - threshold;
  return threshold + span * Math.pow((product - threshold) / span, k);
}
